import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { Shield, LogIn, ChevronDown } from 'lucide-react';
import NeuraliaLogo from './NeuraliaLogo';
import { useAuth } from '../context/AuthContext';

const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [recursosOpen, setRecursosOpen] = useState(false);
  const recursosRef = useRef(null);
  const location = useLocation();
  const isHome = location.pathname === '/';
  const { user } = useAuth();
  const userPanelRoute = user?.role === 'admin'
    ? '/admin'
    : ['doctora', 'enfermera', 'administradora', 'community_manager'].includes(user?.role)
      ? '/clinica/panel'
      : user?.role === 'agencia'
        ? '/agencias/panel'
        : user?.role === 'partner' || user?.role === 'asociado'
          ? '/partners/panel'
          : '/panel';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Cierra el dropdown si se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (recursosRef.current && !recursosRef.current.contains(e.target)) {
        setRecursosOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navLink = "text-sm text-text-body hover:text-primary transition-transform duration-200 inline-block whitespace-nowrap hover:-translate-y-0.5 will-change-transform";

  const RECURSOS_LINKS = [
    { to: '/tendencias',          label: 'Tendencias 2026',       desc: 'Datos verificados del sector y hoja de ruta' },
    { to: '/developers',          label: 'Developers · MCP',      desc: 'Conecta herramientas a Aynara vía MCP' },
    { to: '/recursos',            label: 'Recursos y artículos',  desc: 'Guías y análisis técnicos' },
    { to: '/integraciones/deepseek', label: 'Integración DeepSeek', desc: 'Calculadora y comparativa de modelos' },
    { to: '/experiencia',         label: 'Experiencia 3D',        desc: 'Explora el ecosistema en modo inmersivo' },
  ];

  const desktopLinks = (
    <>
      <Link to="/nosotros" className={navLink}>Nosotros</Link>
      <Link to="/precios" className={navLink} data-testid="nav-servicios-link">Servicios IA</Link>
      <Link to="/sistema-multiagente" className={navLink} data-testid="nav-multiagente-link">Sistema Multiagente</Link>
      <Link to="/seguridad-agentes" className={navLink} data-testid="nav-seguridad-link">Seguridad</Link>
      <Link to="/academy" className={navLink} data-testid="nav-academy-link">Academy</Link>

      {/* ── Dropdown Recursos ── */}
      <div ref={recursosRef} className="relative" data-testid="nav-recursos-dropdown">
        <button
          onClick={() => setRecursosOpen(v => !v)}
          className={`${navLink} flex items-center gap-1 bg-transparent border-0 p-0 cursor-pointer`}
          data-testid="nav-recursos-btn"
          aria-haspopup="true"
          aria-expanded={recursosOpen}
        >
          Recursos
          <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${recursosOpen ? 'rotate-180' : ''}`} />
        </button>
        <AnimatePresence>
          {recursosOpen && (
            <motion.div
              initial={{ opacity: 0, y: 8, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 6, scale: 0.97 }}
              transition={{ duration: 0.15 }}
              className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-64 rounded-2xl border border-white/10 bg-[#0a1628]/95 backdrop-blur-xl shadow-[0_16px_48px_rgba(0,0,0,0.5)] overflow-hidden z-50"
              data-testid="nav-recursos-panel"
            >
              <div className="p-2">
                {RECURSOS_LINKS.map(({ to, label, desc }) => (
                  <Link
                    key={to}
                    to={to}
                    onClick={() => setRecursosOpen(false)}
                    className="flex flex-col gap-0.5 px-3 py-2.5 rounded-xl hover:bg-white/[0.06] transition-colors group"
                    data-testid={`nav-recursos-link-${to.replace(/\//g, '-').slice(1)}`}
                  >
                    <span className="text-sm font-semibold text-white group-hover:text-cyan-300 transition-colors">{label}</span>
                    <span className="text-xs text-slate-500">{desc}</span>
                  </Link>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <Link to="/partners" className={navLink}>Partners</Link>
      <Link to="/agencias" className={navLink}>Agencias</Link>
      <Link to="/contacto" className="btn-secondary !py-1.5 !px-4 !text-sm whitespace-nowrap" data-testid="nav-contact-link">Contactar</Link>
      <div className="flex items-center gap-3">
        <Link to={user ? userPanelRoute : '/login'} className="flex items-center gap-1 text-sm text-text-body hover:text-primary transition-colors whitespace-nowrap" data-testid="nav-login-link">
          <LogIn className="w-3.5 h-3.5" /> Acceso
        </Link>
        <Link to={user?.role === 'admin' ? '/admin' : '/acceso-admin'} className="flex items-center gap-1 text-sm text-text-body hover:text-primary transition-colors whitespace-nowrap" data-testid="nav-admin-access-link">
          <Shield className="w-3.5 h-3.5" /> Admin
        </Link>
      </div>
    </>
  );

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className="fixed top-5 left-0 right-0 z-50 transition-all duration-300"
    >
      <div className={`max-w-7xl mx-auto px-4 lg:px-6 py-2.5 flex items-center justify-between rounded-full border backdrop-blur-xl ${
        scrolled || !isHome ? 'bg-background/82 border-border-highlight shadow-[0_12px_40px_rgba(0,0,0,0.28)]' : 'bg-background/64 border-border-subtle'
      }`}>
        <Link to="/" className="flex items-center gap-2 shrink-0 mr-3" data-testid="logo-link">
          <NeuraliaLogo size={38} />
          <span className="font-heading font-bold text-xl tracking-tight text-text-heading whitespace-nowrap">
            Neuralia <span className="bg-[linear-gradient(90deg,#4285F4_0%,#EA4335_33%,#FBBC05_66%,#34A853_100%)] bg-clip-text text-transparent">369</span>
          </span>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-4 flex-nowrap">
          {desktopLinks}
        </div>

        {/* Mobile Menu Button */}
        <button
          className="lg:hidden p-2 text-text-body hover:text-primary transition-colors"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </button>
      </div>

      {/* Mobile Menu — deduplicated */}
      <motion.div
        initial={false}
        animate={{ height: mobileMenuOpen ? 'auto' : 0, opacity: mobileMenuOpen ? 1 : 0 }}
        className="lg:hidden overflow-hidden bg-background/95 backdrop-blur-xl border-b border-border-subtle"
      >
        <div className="px-golden-md py-golden-lg flex flex-col gap-golden-md">
          <Link to="/" className="text-text-body hover:text-primary transition-colors py-2" onClick={() => setMobileMenuOpen(false)}>
            Inicio
          </Link>
          <Link to="/precios" className="text-text-body hover:text-primary transition-colors py-2" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-servicios-link">
            Servicios IA
          </Link>
          <Link to="/sistema-multiagente" className="text-text-body hover:text-primary transition-colors py-2" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-multiagente-link">
            Sistema Multiagente
          </Link>
          <Link to="/seguridad-agentes" className="text-text-body hover:text-primary transition-colors py-2" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-seguridad-link">
            Seguridad
          </Link>
          <Link to="/academy" className="text-text-body hover:text-primary transition-colors py-2" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-academy-link">
            Academy
          </Link>
          <div className="border-t border-white/6 pt-2 mt-1">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2 px-1">Recursos</p>
            <Link to="/tendencias" className="text-text-body hover:text-primary transition-colors py-2 block" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-tendencias-link">
              Tendencias 2026
            </Link>
            <Link to="/developers" className="text-text-body hover:text-primary transition-colors py-2 block" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-developers-link">
              Developers · MCP
            </Link>
            <Link to="/recursos" className="text-text-body hover:text-primary transition-colors py-2 block" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-recursos-link">
              Recursos y artículos
            </Link>
            <Link to="/integraciones/deepseek" className="text-text-body hover:text-primary transition-colors py-2 block" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-deepseek-link">
              Integración DeepSeek
            </Link>
          </div>
          <Link to="/nosotros" className="text-text-body hover:text-primary transition-colors py-2" onClick={() => setMobileMenuOpen(false)}>
            Nosotros
          </Link>
          <Link to="/experiencia" className="text-text-body hover:text-primary transition-colors py-2" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-experiencia-link">
            Experiencia 3D
          </Link>
          <Link to="/partners" className="text-text-body hover:text-primary transition-colors py-2" onClick={() => setMobileMenuOpen(false)}>
            Partners
          </Link>
          <Link to="/agencias" className="text-text-body hover:text-primary transition-colors py-2" onClick={() => setMobileMenuOpen(false)}>
            Agencias
          </Link>
          <Link to={user ? userPanelRoute : '/login'}
            className="flex items-center gap-2 text-text-body hover:text-primary transition-colors py-2"
            onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-login-link">
            <LogIn className="w-4 h-4" /> Acceso
          </Link>
          <Link to={user?.role === 'admin' ? '/admin' : '/acceso-admin'}
            className="flex items-center gap-2 text-text-body hover:text-primary transition-colors py-2"
            onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-admin-access-link">
            <Shield className="w-4 h-4" /> Acceso admin
          </Link>
          <Link to="/contacto" className="btn-primary justify-center mt-golden-sm" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-contact-link">
              Contactar
          </Link>
        </div>
      </motion.div>
    </motion.nav>
  );
};

export default Navigation;
